import React, { FC, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Link, useParams } from 'react-router'

import { Bar } from '@ant-design/plots'
import { Col, Flex, Image, Row } from 'antd'
import { useResponsive } from 'antd-style'

import 'dayjs/locale/uk'

import placeholder from '../../components/icon/assets/rider.png'
import { HTML } from '../../components/markup/HTML'
import { Page } from '../../components/page'
import { Title } from '../../components/text/Title'
import { useRiderQuery } from '../../graphql'
import { Context as ConfigContext } from '../../providers/Config'
import routes from '../../routes'
import NotFound from '../NotFound'

import { Achievements } from './Achievements'
import { Career } from './Career'
import { Debut } from './Debut'
import { FamilyMembers } from './FamilyMembers'
import { Information } from './Information'
import { Statistics } from './Statistics'

type TrackStats = Record<string, { count: number } & Partial<TrackCardFragment>>

const Rider: FC = () => {
  const { formatMessage } = useIntl()
  const { mobile } = useResponsive()
  const { slug = '' } = useParams()
  const { theme } = useContext(ConfigContext)
  const { data, loading } = useRiderQuery({ variables: { slug } })
  const rider = data?.rider

  const bestTracks = useMemo(() => {
    const trackInfo: TrackStats = {}
    rider?.results.forEach((it) => {
      if (it?.position && it?.position === 1) {
        const count = Number(trackInfo[`${it?.track?.name}`]?.count) || 0
        if (it?.track?.name) {
          trackInfo[it.track.name] = { count: count + 1, ...it.track }
        }
      }
    })

    return Object.values(trackInfo)
  }, [rider?.results])

  if ((!data?.rider || !data?.rider?.name) && !loading) return <NotFound />

  return (
    <Flex gap={16} vertical>
      <Page
        loading={loading}
        title={rider?.name}
        breadcrumbs={[{ title: <Link to={routes.riders}>{formatMessage({ id: 'riders.header' })}</Link> }]}
        seo={{
          ...rider?.seo,
          metaTitle: data?.rider?.name
            ? [data?.rider?.name, formatMessage({ id: 'riders.seo.title' })].join(' | ')
            : '',
        }}
      >
        <Row gutter={[32, 32]} align={'top'}>
          <Col xs={mobile ? (rider?.photo?.formats?.large?.url ? 24 : 0) : 24} lg={8}>
            <Image
              style={{ objectFit: 'cover', objectPosition: 'top' }}
              width={'100%'}
              height={560}
              preview={false}
              src={rider?.photo?.formats?.large?.url ?? rider?.photo?.url ?? placeholder}
            />
          </Col>
          <Col xs={24} lg={8}>
            <Row gutter={[16, 16]}>
              <Col xs={{ order: 0, span: 24 }} lg={{ order: 1, span: 24 }}>
                <Information data={rider} />
              </Col>
              <Col xs={{ order: 1, span: 24 }} lg={{ order: 2, span: 24 }}>
                <FamilyMembers data={rider?.familyMembers} />
              </Col>
              <Col xs={{ order: 2, span: 24 }} lg={{ order: 0, span: 24 }}>
                <Achievements data={rider?.stats} />
              </Col>
            </Row>
          </Col>
          <Col xs={24} lg={8}>
            <Debut data={rider?.stats} />
          </Col>
          {rider?.career && rider.statistics && (
            <Col span={24}>
              <Flex vertical gap={16}>
                <Title level={3}>{formatMessage({ id: 'rider.career' })}</Title>
                <Career career={rider?.career} statistics={rider?.statistics} />
              </Flex>
            </Col>
          )}
          {bestTracks.length > 0 ? (
            <Col span={24}>
              <Title level={3}>{formatMessage({ id: 'rider.tracks.wins' })}</Title>
              <Bar
                theme={theme}
                data={bestTracks}
                height={24 * bestTracks.length + 48}
                sort={{
                  reverse: true,
                  by: 'y',
                }}
                legend={false}
                xField={'name'}
                yField={'count'}
                colorField={'name'}
              />
            </Col>
          ) : null}
          <Col span={24}>
            <Statistics data={rider?.races} />
          </Col>
          <Col span={24}>
            <HTML>{rider?.description}</HTML>
          </Col>
        </Row>
      </Page>
    </Flex>
  )
}

export { Rider as default }
